/**
 * SimpleAdapter — real RuntimeAdapter implementation using nostr-tools SimplePool.
 * Ported from the nostr-canvas reference app.
 */

import {
  type Filter,
  type NostrEvent,
  SimplePool,
  type UnsignedEvent,
} from 'nostr-tools';
import type {
  CapabilityOptions,
  FetchRequest,
  FetchResult,
  NavigateResult,
  NavigateTarget,
  ProfileData,
  RuntimeAdapter,
} from '@soapbox.pub/nostr-canvas';
import type { Signer } from '@/lib/signer';
import { uploadToBlossom } from '@/lib/blossom-upload';
import { pickFile } from '@/lib/file-picker';

function filterKey(filter: Filter): string {
  const sorted = Object.fromEntries(
    Object.entries(filter)
      .map(([k, v]): [string, unknown] => [k, Array.isArray(v) ? [...v].sort() : v])
      .sort(([a], [b]) => a.localeCompare(b)),
  );
  return JSON.stringify(sorted);
}

type SubSlot = {
  close: () => void;
  callbacks: Set<(event: NostrEvent) => void>;
};

export class SimpleAdapter implements RuntimeAdapter {
  private pool = new SimplePool();
  private relays: string[];
  private signer: Signer | null = null;
  private profileCache = new Map<string, ProfileData>();
  private profileCallbacks = new Map<
    string,
    Set<(pubkey: string, profile: ProfileData) => void>
  >();
  private subSlots = new Map<string, SubSlot>();
  private profileSubs = new Map<string, { close: () => void }>();

  onNavigate: ((target: NavigateTarget) => NavigateResult | null) | null = null;

  constructor(relays: string[]) {
    this.relays = relays;
  }

  setRelays(relays: string[]): void {
    this.relays = relays;
  }

  setSigner(signer: Signer | null): void {
    this.signer = signer;
  }

  subscribe(filter: Filter, onEvent: (event: NostrEvent) => void): () => void {
    const key = filterKey(filter);
    let slot = this.subSlots.get(key);
    if (!slot) {
      const callbacks = new Set<(event: NostrEvent) => void>();
      const sub = this.pool.subscribe(this.relays, filter, {
        onevent: (event) => callbacks.forEach((cb) => cb(event)),
      });
      slot = { close: () => sub.close(), callbacks };
      this.subSlots.set(key, slot);
    }
    slot.callbacks.add(onEvent);
    return () => {
      slot!.callbacks.delete(onEvent);
      if (slot!.callbacks.size === 0) {
        slot!.close();
        this.subSlots.delete(key);
      }
    };
  }

  async getPublicKey(options?: CapabilityOptions): Promise<string> {
    options?.signal.throwIfAborted();
    if (!this.signer) throw new Error('No signer available');
    const pubkey = await this.signer.getPublicKey();
    options?.signal.throwIfAborted();
    return pubkey;
  }

  async getContacts(): Promise<string[]> {
    if (!this.signer) return [];
    let pubkey: string;
    try {
      pubkey = await this.signer.getPublicKey();
    } catch {
      return [];
    }
    const events = await this.pool.querySync(this.relays, {
      kinds: [3],
      authors: [pubkey],
      limit: 1,
    });
    if (events.length === 0) return [];
    // Keep the newest kind-3 event
    const latest = events.reduce((a, b) => (a.created_at >= b.created_at ? a : b));
    return latest.tags
      .filter(([t, pk]) => t === 'p' && typeof pk === 'string')
      .map(([, pk]) => pk);
  }

  async publishEvent(event: UnsignedEvent, options?: CapabilityOptions): Promise<NostrEvent> {
    options?.signal.throwIfAborted();
    if (!this.signer) throw new Error('No signer available');
    const signed = await this.signer.signEvent(event);
    options?.signal.throwIfAborted();
    await Promise.allSettled(
      this.relays.map((r) => this.pool.publish([r], signed)),
    );
    options?.signal.throwIfAborted();
    return signed;
  }

  async nip44Encrypt(recipientPubkey: string, plaintext: string, options?: CapabilityOptions): Promise<string> {
    options?.signal.throwIfAborted();
    if (!this.signer) throw new Error('No signer available');
    const ciphertext = await this.signer.nip44.encrypt(recipientPubkey, plaintext);
    options?.signal.throwIfAborted();
    return ciphertext;
  }

  async nip44Decrypt(senderPubkey: string, ciphertext: string, options?: CapabilityOptions): Promise<string> {
    options?.signal.throwIfAborted();
    if (!this.signer) throw new Error('No signer available');
    const plaintext = await this.signer.nip44.decrypt(senderPubkey, ciphertext);
    options?.signal.throwIfAborted();
    return plaintext;
  }

  async fetch(request: FetchRequest, options?: CapabilityOptions): Promise<FetchResult> {
    try {
      const res = await globalThis.fetch(request.url, {
        method: request.method ?? 'GET',
        headers: request.headers,
        body: request.body,
        signal: options?.signal,
      });
      const body = await res.text();
      const headers: Record<string, string> = {};
      res.headers.forEach((v, k) => { headers[k] = v; });
      return { ok: true, status: res.status, headers, body };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  getProfile(
    pubkey: string,
    callback: (pubkey: string, profile: ProfileData) => void,
  ): () => void {
    const cached = this.profileCache.get(pubkey);
    if (cached) callback(pubkey, cached);

    const cbs = this.profileCallbacks.get(pubkey) ?? new Set();
    cbs.add(callback);
    this.profileCallbacks.set(pubkey, cbs);

    if (!this.profileSubs.has(pubkey)) {
      const sub = this.pool.subscribe(
        this.relays,
        { kinds: [0], authors: [pubkey], limit: 1 },
        {
          onevent: (event) => {
            try {
              const profile = JSON.parse(event.content) as ProfileData;
              this.profileCache.set(pubkey, profile);
              this.profileCallbacks.get(pubkey)?.forEach((cb) => cb(pubkey, profile));
            } catch { /* ignore malformed */ }
          },
        },
      );
      this.profileSubs.set(pubkey, { close: () => sub.close() });
    }

    return () => {
      cbs.delete(callback);
      if (cbs.size === 0) {
        this.profileSubs.get(pubkey)?.close();
        this.profileSubs.delete(pubkey);
        this.profileCallbacks.delete(pubkey);
      }
    };
  }

  async navigate(target: NavigateTarget, options?: CapabilityOptions): Promise<NavigateResult> {
    options?.signal.throwIfAborted();
    if (this.onNavigate) {
      const result = this.onNavigate(target);
      if (result) {
        options?.signal.throwIfAborted();
        return result;
      }
    }
    return { ok: false, reason: 'not_implemented' };
  }

  async uploadImage(options?: CapabilityOptions): Promise<string> {
    options?.signal?.throwIfAborted();
    if (!this.signer) throw new Error('No signer available');

    const file = await pickFile('image/*', options?.signal);

    options?.signal?.throwIfAborted();
    return uploadToBlossom(this.signer, file, { signal: options?.signal });
  }

  async fetchEvents(filter: Filter): Promise<NostrEvent[]> {
    return this.pool.querySync(this.relays, filter);
  }

  destroy(): void {
    this.pool.destroy();
  }
}
